<template>
  <dpc-swipeable
    class="button-date-select"
    :hide-gradient="true"
    type="scrollLeft"
    aria-hidden="true"
    ref="swipeable"
    v-model:x="currentX"
  >
    <div
      v-for="(context, key) in yearMonthContextList"
      :key="key"
      class="button-date-select__month-list"
    >
      <div class="button-date-select__button-month button-month">
        <span class="button-month__year">{{ context.meta.year }}</span>
        <span class="button-month__month">{{ context.meta.shortMonth }}</span>
      </div>
      <div class="button-date-select__day-list">
        <dpc-button-date-select-option
          v-for="(isoDate, index) in context.isoDates"
          :key="`button-date-select-${index}`"
          :iso-date="isoDate"
          :iso-date-selected="isoDateInternal"
          :iso-dates-with-content="isoDatesWithContent"
          @select="selectDate"
        />
      </div>
    </div>
  </dpc-swipeable>
</template>

<script setup>
import dayjs from 'dayjs';
import { computed, ref, toRefs, watch, markRaw } from 'vue';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import { gsap } from '@gsap/shockingly';
import { Draggable } from '@gsap/shockingly/Draggable';
import { getDayjsByDate } from '@utils/date';
import DpcButtonDateSelectOption from './dpc-button-date-select-option.vue';

const props = defineProps({
  isoDateStart: {
    type: String,
    required: true,
  },
  isoDateEnd: {
    type: String,
    required: true,
  },
  isoDateSelected: {
    type: String,
  },
  isoDatesWithContent: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['update:isoDateSelected']);

const { isoDateStart, isoDateEnd, isoDateSelected } = toRefs(props);
const isoDateInternal = ref(isoDateSelected.value);

const duration = computed(() => {
  const difference = getDayjsByDate(isoDateEnd.value).diff(
    getDayjsByDate(isoDateStart.value)
  );

  return dayjs.duration(difference);
});

// +1 because we want the end date to also show up
const durationAsDays = computed(() => Math.ceil(duration.value.asDays()) + 1);

const dayjsDates = computed(() =>
  Array(durationAsDays.value)
    .fill(null)
    .map((_, index) => {
      const date = getDayjsByDate(isoDateStart.value).add(index, 'day');
      return date;
    })
);

const yearMonthContextList = computed(() => {
  const dayjsDatesGrouped = groupBy(dayjsDates.value, dayjsDate =>
    dayjsDate.format('YYYY-MM')
  );
  const yearMonthContextList = mapValues(dayjsDatesGrouped, dayjsDates => ({
    meta: {
      year: dayjsDates[0].format('YYYY'),
      shortMonth: dayjsDates[0].format('MMM'),
    },
    isoDates: dayjsDates.map(dayjsDate => dayjsDate.format('YYYY-MM-DD')),
  }));
  return yearMonthContextList;
});

const swipeable = ref(undefined);
const currentX = ref(0);

const scrollToDate = isoDate => {
  // TODO: find right place for this
  const targetElement = swipeable.value.$el.querySelector(
    `[data-date="${isoDate}"]`
  );
  const swipeableContentElement =
    swipeable.value.$el.querySelector(`.swipeable__content`);
  if (!swipeableContentElement) return;

  const draggable = Draggable.get(swipeableContentElement);
  const xTarget = gsap.getProperty(targetElement, 'offsetLeft');
  gsap.to(swipeableContentElement, {
    scrollLeft: xTarget,
    onUpdate() {
      draggable.update();
      currentX.value = -this.vars.scrollLeft;
    },
  });
};

const selectDate = date => {
  isoDateInternal.value = date;
  emit('update:isoDateSelected', date);
};

watch(isoDateSelected, isoDateSelected => {
  isoDateInternal.value = isoDateSelected;
});

watch(isoDateInternal, isoDateInternal => {
  scrollToDate(isoDateInternal);
});
</script>
